import { STATUS_LABEL, type TaskWithFlags } from "@desk/contracts";
import type { BoardCardProps, BoardColumnModel, BoardDnd, BoardProps } from "../types";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { DeadlineLabel, SnoozeBadge, SyncBadge } from "@/components/task-bits";
import { openQuickAdd } from "@/features/ui-state";
import { cn } from "@/lib/utils";
import { iconButton } from "./chrome";
import { LabelPill, metaChip } from "./TaskRow";

function Card({ task, card, dnd }: { task: TaskWithFlags; card: BoardCardProps; dnd: BoardDnd }) {
  const d = dnd.useCard(task.id);
  const closed = task.status === "done" || task.status === "cancelled";
  const hasMeta = (task.deadline && !closed) || task.labels.length > 0 || card.sync || task.flags.isSnoozed || card.location;
  return (
    <div
      ref={d.setNodeRef}
      style={d.style}
      {...d.attributes}
      {...d.listeners}
      data-task-id={task.id}
      onClick={card.onOpen}
      className={cn(
        "group/card relative cursor-grab rounded-lg border border-border bg-[var(--console-card)] px-3 pb-2.5 pt-2 text-left outline-none transition-[border-color,background-color] duration-100 active:cursor-grabbing",
        "hover:border-foreground/20 focus-visible:shadow-[0_0_0_1.5px_var(--ring)]",
        card.selected && "border-foreground/35",
        d.isDragging && "cursor-grabbing",
      )}
    >
      <div className="flex h-5 items-center gap-2">
        <span className="text-[12px] leading-none text-subtle tabular">{task.readableId === "DSK-0" ? "—" : task.readableId}</span>
        <div
          className="-mr-1.5 ml-auto opacity-0 transition-opacity focus-within:opacity-100 group-hover/card:opacity-100 [&_button]:size-6"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {card.menu}
        </div>
      </div>
      <div className="mt-1 flex items-start gap-2">
        <PriorityIcon priority={task.priority} className={cn("mt-[2px] size-4 shrink-0", task.priority === "none" && "opacity-60")} />
        <p
          title={task.title}
          className={cn("clamp-2 min-w-0 flex-1 break-words text-[13.5px] font-[450] leading-[20px]", closed ? "text-muted-foreground" : "text-foreground")}
        >
          {task.title}
        </p>
      </div>
      {hasMeta && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
          {task.deadline && !closed && (
            <span className={metaChip}>
              <DeadlineLabel deadline={task.deadline} today={card.today} className="gap-1 [&_svg]:size-3" />
            </span>
          )}
          {task.labels.slice(0, 3).map((l) => (
            <LabelPill key={l} label={l} />
          ))}
          {card.location && (
            <span className={metaChip} title={card.location}>
              <span className="truncate">{card.location}</span>
            </span>
          )}
          <SnoozeBadge task={task} timezone={card.timezone} />
          <SyncBadge mark={card.sync} />
        </div>
      )}
    </div>
  );
}

function Column({ col, card, dnd }: { col: BoardColumnModel; card: BoardProps["card"]; dnd: BoardDnd }) {
  const drop = dnd.useColumn(col.status);
  const closed = col.status === "done" || col.status === "cancelled";
  return (
    <section
      ref={drop.setNodeRef}
      aria-label={`${col.label}, ${col.tasks.length} tasks`}
      className={cn(
        "group/col flex w-[300px] shrink-0 flex-col rounded-lg p-1.5 pt-0 transition-colors duration-100",
        drop.isOver ? "bg-selected" : "bg-[var(--console-column)]",
      )}
    >
      <header className="flex h-10 items-center gap-2 px-1.5">
        <StatusIcon status={col.status} className="size-3.5" />
        <h2 className="font-sans text-[13px] font-medium tracking-normal text-foreground">{col.label}</h2>
        <span className="text-[12.5px] text-subtle tabular">{col.tasks.length}</span>
        {!closed && (
          <button
            type="button"
            aria-label={`New task in ${STATUS_LABEL[col.status]}`}
            onClick={() => openQuickAdd({ status: col.status })}
            className={cn(iconButton, "ml-auto size-6 opacity-0 focus-visible:opacity-100 group-hover/col:opacity-100 [&_svg]:size-3.5")}
          >
            <svg viewBox="0 0 16 16" aria-hidden>
              <path d="M8 3.25v9.5M3.25 8h9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        )}
      </header>
      <div className="flex min-h-20 flex-col gap-1.5">
        {col.tasks.map((t) => (
          <Card key={t.id} task={t} card={card(t)} dnd={dnd} />
        ))}
      </div>
    </section>
  );
}

export function ConsoleBoard({ columns, card, dnd, empty }: BoardProps) {
  const total = columns.reduce((n, c) => n + c.tasks.length, 0);
  return (
    <div data-wide className="pt-3">
      {total === 0 && empty}
      <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-6">
        {columns.map((c) => (
          <Column key={c.status} col={c} card={card} dnd={dnd} />
        ))}
      </div>
    </div>
  );
}
